import { SITE_URL, SITE_NAME, truncate } from '@/seo/config';

const place = (commune) => ({
  '@type': 'Place',
  name: commune,
  address: { '@type': 'PostalAddress', addressLocality: commune, addressRegion: 'La Réunion', addressCountry: 'FR' },
});

export const postJsonLd = (post) => {
  const url = `${SITE_URL}/post/${post.post_id}`;
  const author = { '@type': 'Person', name: post.user_name, url: `${SITE_URL}/profile/${post.user_id}` };
  const base = {
    '@context': 'https://schema.org',
    name: post.title,
    description: truncate(post.content, 300),
    url,
    image: post.image_url || undefined,
    inLanguage: 'fr',
  };
  if (post.category === 'evenement' && post.event_date) {
    return {
      ...base,
      '@type': 'Event',
      startDate: post.event_date,
      eventStatus: 'https://schema.org/EventScheduled',
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      location: place(post.commune),
      organizer: author,
    };
  }
  return {
    ...base,
    '@type': 'SocialMediaPosting',
    headline: post.title,
    articleBody: post.content,
    datePublished: post.created_at,
    author,
    publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
    contentLocation: place(post.commune),
    mainEntityOfPage: url,
    interactionStatistic: [
      { '@type': 'InteractionCounter', interactionType: 'https://schema.org/LikeAction', userInteractionCount: post.likes_count || 0 },
      { '@type': 'InteractionCounter', interactionType: 'https://schema.org/CommentAction', userInteractionCount: post.comments_count || 0 },
    ],
  };
};

export const profileJsonLd = (u) => {
  const url = `${SITE_URL}/profile/${u.user_id}`;
  const entity = u.is_commerce
    ? {
        '@type': 'LocalBusiness',
        name: u.commerce_name || u.name,
        description: u.commerce_description || u.bio || undefined,
        image: u.picture || undefined,
        telephone: u.commerce_phone || undefined,
        url: u.commerce_website || url,
        openingHours: u.commerce_hours || undefined,
        address: { '@type': 'PostalAddress', streetAddress: u.commerce_address || undefined, addressLocality: u.commune, addressRegion: 'La Réunion', addressCountry: 'FR' },
      }
    : {
        '@type': 'Person',
        name: u.name,
        description: u.bio || undefined,
        image: u.picture || undefined,
        url,
        homeLocation: place(u.commune),
        interactionStatistic: { '@type': 'InteractionCounter', interactionType: 'https://schema.org/FollowAction', userInteractionCount: u.followers_count || 0 },
      };
  return { '@context': 'https://schema.org', '@type': 'ProfilePage', url, mainEntity: entity };
};

export const communeJsonLd = (commune, posts) => {
  const url = `${SITE_URL}/commune/${commune.slug}`;
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'City',
      name: commune.name,
      url,
      containedInPlace: { '@type': 'AdministrativeArea', name: 'La Réunion' },
      address: { '@type': 'PostalAddress', addressLocality: commune.name, addressRegion: 'La Réunion', addressCountry: 'FR' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${commune.name} : alertes, événements et bons plans`,
      url,
      inLanguage: 'fr',
      about: { '@type': 'City', name: commune.name },
      mainEntity: {
        '@type': 'ItemList',
        numberOfItems: posts.length,
        itemListElement: posts.slice(0, 30).map((p, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          url: `${SITE_URL}/post/${p.post_id}`,
          name: p.title,
        })),
      },
    },
  ];
};

export const eventsJsonLd = (events) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Événements à La Réunion',
  url: `${SITE_URL}/calendar`,
  numberOfItems: events.length,
  itemListElement: events.slice(0, 50).map((e, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    item: {
      '@type': 'Event',
      name: e.title,
      description: truncate(e.content, 200),
      startDate: e.event_date,
      url: `${SITE_URL}/post/${e.post_id}`,
      image: e.image_url || undefined,
      location: place(e.commune),
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    },
  })),
});
